// ポータルの DOM モーダル共通部品（見た目と小道具）。
// canvas 内テキスト入力では日本語IMEが使えないため、ポータル側のモーダルは通常の DOM で作る。
// 登録モーダルと確認モーダルが同じ枠・同じボタンを使うので、ここへ切り出した。

export function el(tag: string, className: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** 全画面オーバーレイ＋中央パネルを組んで body へ挿す。戻り値の `close()` で片付ける。 */
export function openOverlay(): { overlay: HTMLElement; panel: HTMLElement; close: () => void } {
  const overlay = el('div', 'hs-modal-overlay');
  overlay.innerHTML = MODAL_CSS;
  const panel = el('div', 'hs-modal');
  overlay.append(panel);
  document.body.append(overlay);
  return { overlay, panel, close: () => overlay.remove() };
}

export const MODAL_CSS = `<style>
  .hs-modal-overlay {
    position: fixed; inset: 0; z-index: 9999;
    display: flex; align-items: center; justify-content: center;
    background: rgba(20, 32, 28, .55); backdrop-filter: blur(2px);
    font-family: system-ui, -apple-system, "Hiragino Kaku Gothic ProN", "Yu Gothic", Meiryo, sans-serif;
  }
  .hs-modal {
    width: min(92vw, 440px); box-sizing: border-box;
    background: #FBF8F1; color: #22302B;
    border-radius: 18px; padding: 26px 26px 22px;
    box-shadow: 0 24px 60px rgba(20, 32, 28, .4);
    display: flex; flex-direction: column; gap: 10px;
    max-height: 90vh; overflow-y: auto;
  }
  .hs-modal-title {
    font-family: ui-rounded, "SF Pro Rounded", "Hiragino Maru Gothic ProN", system-ui, sans-serif;
    font-size: 22px; font-weight: 700; text-align: center;
  }
  .hs-modal-hint { font-size: 13px; color: #5E6C63; text-align: center; margin-bottom: 6px; }
  .hs-modal-body { font-size: 13.5px; line-height: 1.7; color: #22302B; }
  .hs-modal-body ul { margin: 8px 0; padding-left: 1.25em; }
  .hs-modal-body li { margin-bottom: 3px; }
  .hs-modal-warn {
    background: #FDECEA; border-left: 4px solid #C0392B; border-radius: 0 8px 8px 0;
    padding: 10px 14px; font-size: 13px; line-height: 1.6; color: #8B2A1E;
  }
  /* 統合の確認で「★ 5 → 12」を出す1行。**この数字が押す判断の全て**なので大きく見せる。 */
  .hs-merge-stars {
    font-family: ui-rounded, "SF Pro Rounded", "Hiragino Maru Gothic ProN", system-ui, sans-serif;
    font-size: 20px; font-weight: 700; text-align: center; color: #0A6E49;
    background: #DDF0E5; border-radius: 12px; padding: 12px 14px; margin: 12px 0 8px;
  }
  /* 引き継ぎコード。**この1回しか表示されない**ので、写し取りやすさを最優先にする
     （等幅・大きめ・字間を広げる・タップ1回で全選択できる user-select: all）。
     ⚠️ この注記はテンプレートリテラルの中なので、バッククォートを書かないこと。 */
  .hs-transfer-code {
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: clamp(19px, 5.6vw, 25px); font-weight: 700; letter-spacing: .06em;
    text-align: center; color: #0A6E49; background: #DDF0E5;
    border-radius: 12px; padding: 16px 10px; margin: 10px 0 6px;
    user-select: all; -webkit-user-select: all; word-break: break-all;
  }
  .hs-code-input { font-family: ui-monospace, "SF Mono", Menlo, monospace; letter-spacing: .06em; text-align: center; }
  .hs-field-label { font-size: 12px; font-weight: 700; color: #5E6C63; margin-top: 6px; letter-spacing: .02em; }
  .hs-input {
    width: 100%; box-sizing: border-box; height: 46px; font-size: 18px;
    border-radius: 10px; border: 2px solid #E3DAC7; background: #fff; color: #22302B;
    padding: 4px 12px; outline: none;
  }
  .hs-input:focus { border-color: #0E9E68; }
  .hs-select-wrap {
    position: relative; display: flex; align-items: center;
    border: 2px solid #E3DAC7; border-radius: 10px; background: #fff; overflow: hidden;
  }
  .hs-select-wrap:focus-within { border-color: #0E9E68; }
  .hs-select-flag { font-size: 22px; padding: 0 4px 0 12px; }
  .hs-select {
    flex: 1; appearance: none; -webkit-appearance: none;
    border: 0; background: transparent; outline: none;
    height: 46px; font-size: 17px; color: #22302B; padding: 4px 12px 4px 8px; cursor: pointer;
  }
  /* 端末（パスキー）一覧。削除は取り消せないので、行の境目と「この端末」の印を強く出す。 */
  .hs-devices { display: flex; flex-direction: column; gap: 8px; margin: 6px 0 12px; }
  .hs-device { border: 1px solid #E3DAC7; border-radius: 10px; padding: 10px 12px; }
  .hs-device.current { border-color: #0E9E68; background: #F2FAF6; }
  .hs-device-head { display: flex; align-items: center; gap: 8px; }
  .hs-device-name { font-weight: 700; font-size: 14px; }
  .hs-device-badge { font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 999px; background: #DDF0E5; color: #0A6E49; }
  .hs-device-meta { font-size: 11.5px; color: #5E6C63; margin-top: 2px; }
  .hs-device-warn { font-size: 12px; color: #8B2A1E; background: #FDECEA; border-radius: 8px; padding: 8px 10px; margin-top: 8px; line-height: 1.5; }
  .hs-device-actions { display: flex; justify-content: flex-end; margin-top: 4px; }
  .hs-linkbtn { font: inherit; font-size: 12.5px; background: none; border: 0; padding: 2px 0; cursor: pointer; color: #5E6C63; border-bottom: 1px solid transparent; }
  .hs-linkbtn:hover:not(:disabled) { border-bottom-color: currentColor; }
  .hs-linkbtn.danger { color: #C0392B; }
  .hs-linkbtn:disabled { opacity: .5; cursor: not-allowed; }
  .hs-devices-all { width: 100%; margin-top: 4px; font-size: 13.5px; }
  .hs-devices-all.danger { color: #C0392B; border-color: #C0392B; }
  .hs-field-note { font-size: 11.5px; color: #5E6C63; margin-top: 2px; line-height: 1.4; }
  .hs-consent { font-size: 11.5px; color: #5E6C63; margin-top: 4px; line-height: 1.5; text-align: center; }
  .hs-consent a { color: #0A6E49; }
  .hs-modal-err { min-height: 16px; font-size: 12.5px; color: #C0392B; text-align: center; }
  .hs-modal-row { display: flex; gap: 12px; justify-content: center; margin-top: 4px; }
  .hs-btn {
    font-family: ui-rounded, "SF Pro Rounded", "Hiragino Maru Gothic ProN", system-ui, sans-serif;
    font-size: 15px; font-weight: 700; border: 0; cursor: pointer;
    border-radius: 11px; padding: 12px 24px;
  }
  .hs-btn:disabled { opacity: .5; cursor: not-allowed; }
  .hs-btn-primary { background: #0E9E68; color: #fff; box-shadow: 0 8px 18px rgba(14,158,104,.35); }
  .hs-btn-primary:hover:not(:disabled) { background: #0C8D5D; }
  .hs-btn-ghost { background: transparent; color: #22302B; border: 1px solid #E3DAC7; }
  .hs-btn-ghost:hover { border-color: #0E9E68; color: #0A6E49; }
  .hs-btn-danger { background: #C0392B; color: #fff; box-shadow: 0 8px 18px rgba(192,57,43,.3); }
  .hs-btn-danger:hover:not(:disabled) { background: #A5301F; }
  @media (prefers-color-scheme: dark) {
    .hs-modal { background: #1C2A25; color: #E9EFE9; }
    .hs-modal-hint, .hs-field-label, .hs-field-note, .hs-consent { color: #A6B2AA; }
    .hs-modal-body { color: #E9EFE9; }
    .hs-modal-warn { background: #3A211C; color: #F3B5AC; }
    .hs-merge-stars, .hs-transfer-code { background: #163027; color: #7FE3B6; }
    .hs-consent a { color: #7FE3B6; }
    .hs-input, .hs-select-wrap { background: #14201C; border-color: #2C3B34; }
    .hs-input, .hs-select { color: #E9EFE9; }
    .hs-btn-ghost { color: #E9EFE9; border-color: #2C3B34; }
    .hs-device { border-color: #2C3B34; }
    .hs-device.current { border-color: #2BC488; background: #163027; }
    .hs-device-badge { background: #163027; color: #7FE3B6; }
    .hs-device-meta, .hs-linkbtn { color: #A6B2AA; }
    .hs-device-warn { background: #3A211C; color: #F3B5AC; }
    .hs-linkbtn.danger { color: #E8796B; }
    .hs-devices-all.danger { color: #E8796B; border-color: #E8796B; }
  }
</style>`;
